import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  FormGroup,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {Link as RouterLink} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {GET_MY_NOTIFICATION_PREFERENCES} from '../graphql/queries';
import {UPDATE_NOTIFICATION_PREFERENCES} from '../graphql/mutations';
import {useToast} from '../contexts/ToastContext';

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  push: 'Push Notifications',
  in_app: 'In-App',
  slack: 'Slack',
};

const NotificationSettingsPage: React.FC = () => {
  const {showToast} = useToast();
  const {data, loading} = useQuery(GET_MY_NOTIFICATION_PREFERENCES);
  const [updatePrefs, {loading: saving}] = useMutation(UPDATE_NOTIFICATION_PREFERENCES);

  const [channels, setChannels] = useState<string[]>(['email', 'in_app']);
  const [digestFrequency, setDigestFrequency] = useState('immediate');
  const [quietHoursStart, setQuietHoursStart] = useState('');
  const [quietHoursEnd, setQuietHoursEnd] = useState('');

  useEffect(() => {
    if (data?.myNotificationPreferences) {
      const p = data.myNotificationPreferences;
      setChannels(p.channels);
      setDigestFrequency(p.digestFrequency);
      setQuietHoursStart(p.quietHoursStart || '');
      setQuietHoursEnd(p.quietHoursEnd || '');
    }
  }, [data]);

  const toggleChannel = (ch: string) => {
    setChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
    );
  };

  const handleSave = async () => {
    try {
      await updatePrefs({
        variables: {
          channels,
          digestFrequency,
          quietHoursStart: quietHoursStart || null,
          quietHoursEnd: quietHoursEnd || null,
        },
      });
      showToast({message: 'Preferences saved', severity: 'success', autoHideDuration: 3000});
    } catch (err: any) {
      showToast({message: err.message || 'Failed to save', severity: 'error', autoHideDuration: 5000});
    }
  };

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', mt: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{maxWidth: 640, mx: 'auto', p: 3}}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{mb: 2}}
        aria-label="breadcrumb"
      >
        <MuiLink
          component={RouterLink}
          to="/app/settings"
          underline="hover"
          color="inherit"
        >
          Settings
        </MuiLink>
        <Typography color="text.primary">Notifications</Typography>
      </Breadcrumbs>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 3}}>
        <NotificationsIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          Notification Settings
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{p: 3, mb: 3}}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Channels
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          Choose how you want to receive notifications.
        </Typography>
        <FormGroup>
          {Object.entries(CHANNEL_LABELS).map(([key, label]) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={channels.includes(key)}
                  onChange={() => toggleChannel(key)}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>
      </Paper>

      <Paper variant="outlined" sx={{p: 3, mb: 3}}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Digest Frequency
        </Typography>
        <Select
          value={digestFrequency}
          onChange={(e) => setDigestFrequency(e.target.value)}
          size="small"
          sx={{minWidth: 200}}
        >
          <MenuItem value="immediate">Immediate</MenuItem>
          <MenuItem value="hourly">Hourly Digest</MenuItem>
          <MenuItem value="daily">Daily Digest</MenuItem>
        </Select>
      </Paper>

      <Paper variant="outlined" sx={{p: 3, mb: 3}}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Quiet Hours
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          During quiet hours, only in-app notifications will be delivered (except urgent).
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            label="Start"
            type="time"
            value={quietHoursStart}
            onChange={(e) => setQuietHoursStart(e.target.value)}
            size="small"
            slotProps={{inputLabel: {shrink: true}}}
          />
          <TextField
            label="End"
            type="time"
            value={quietHoursEnd}
            onChange={(e) => setQuietHoursEnd(e.target.value)}
            size="small"
            slotProps={{inputLabel: {shrink: true}}}
          />
        </Stack>
      </Paper>

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving}
        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
      >
        {saving ? 'Saving...' : 'Save Preferences'}
      </Button>
    </Box>
  );
};

export default NotificationSettingsPage;
